import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { consoleApi, ConsoleApiError, type EditableCell, type RegistryDetail } from '@/lib/consoleApi';
import {
  baselineValues, canSubmit, computeChanges, fshMonotonicityError, toEdits, unsignedRequired,
  type CellValue,
} from '@/lib/editConsole';
import { Button } from '@/components/ui/button';

// Known peri sections get a curated label/hint; any other section (e.g. a mode
// id for mode_contraindications) falls back to a prettified heading + no hint.
const SECTION_LABEL: Record<string, string> = {
  fsh_categories: 'FSH cutpoints',
  confound_definitions: 'Confound severity',
  disposition_table: 'Disposition stance',
};
const SECTION_HINT: Record<string, string> = {
  fsh_categories: 'FSH category cutpoints (IU/L), biomarker-driven and age-free. Upper bounds must strictly increase; each category’s lower bound follows the one below it.',
  confound_definitions: 'A driver’s severity is its tier: loud_correctable_driver forces ruled_out; competing_subclinical_driver yields uncertain.',
  disposition_table: 'Sign the stance for the flagged cell. A signed stance is determinate; “leave to LLM” keeps it non-determinate.',
};

function sectionLabel(section: string): string {
  return SECTION_LABEL[section] ?? section.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function stanceLabel(v: string): string {
  if (v === '__defer_llm__') return 'leave to LLM (non-determinate)';
  if (v === 'MA_REQUIRED') return '— unsigned —';
  return v;
}

function determinateFromStance(v: CellValue): boolean {
  const s = String(v);
  return s !== '__defer_llm__' && s !== 'MA_REQUIRED' && s !== '';
}

// change-set display: map enums to their friendly label, truncate long free text
function changeDisplay(kind: string, v: CellValue): string {
  if (kind === 'enum') return stanceLabel(String(v));
  const s = String(v);
  return s.length > 48 ? `${s.slice(0, 48)}…` : s;
}

export function EditableConsole({ detail, onChanged }: { detail: RegistryDetail; onChanged: () => void }) {
  const cells = useMemo(() => detail.editable_cells ?? [], [detail.editable_cells]);
  const [values, setValues] = useState<Record<string, CellValue>>(() => baselineValues(cells));
  const [attested, setAttested] = useState('');
  const [decision, setDecision] = useState<'approve' | 'request_changes'>('approve');
  const [note, setNote] = useState('');
  const [comment, setComment] = useState('');
  const [commentRouted, setCommentRouted] = useState(false);
  const [conflict, setConflict] = useState(false);

  // Re-baseline whenever the server content changes (after a sign / revert /
  // reload) so the change-set resets against fresh values (409 recovery).
  useEffect(() => {
    setValues(baselineValues(cells));
    setConflict(false);
  }, [detail.version_hash, cells]);

  const changes = computeChanges(cells, values);
  const fshError = fshMonotonicityError(cells, values);
  const unsigned = unsignedRequired(cells, values);
  const readiness = canSubmit({
    decision, attestedSignatory: attested, note, fshError, unsignedRequiredCount: unsigned.length,
  });

  const signM = useMutation({
    mutationFn: () => consoleApi.editSign(detail.registry, {
      attested_signatory: attested.trim(), reviewed_hash: detail.version_hash,
      decision, edits: decision === 'approve' ? toEdits(changes) : [], note: note.trim() || null,
    }),
    onSuccess: () => { setNote(''); setAttested(''); onChanged(); },
    onError: (e) => { if (e instanceof ConsoleApiError && e.status === 409) setConflict(true); },
  });

  const revertM = useMutation({
    mutationFn: () => consoleApi.revert(detail.registry, {
      attested_signatory: attested.trim(), reviewed_hash: detail.version_hash,
    }),
    onSuccess: () => onChanged(),
    onError: (e) => { if (e instanceof ConsoleApiError && e.status === 409) setConflict(true); },
  });

  const commentM = useMutation({
    mutationFn: () => consoleApi.structureComment(detail.registry, comment.trim()),
    onSuccess: () => { setComment(''); setCommentRouted(true); },
  });

  const setCell = (path: string, v: CellValue) => {
    setValues((prev) => ({ ...prev, [path]: v }));
    setCommentRouted(false);
  };
  const revertCell = (path: string) => setCell(path, baselineValues(cells)[path] ?? '');

  // Group cells by section, preserving first-seen order (peri sections arrive
  // in their curated order; mode ids in registry order).
  const sectionKeys = cells.reduce<string[]>((acc, c) => (acc.includes(c.section) ? acc : [...acc, c.section]), []);
  const bySection = sectionKeys.map((s) => [s, cells.filter((c) => c.section === s)] as const);

  return (
    <div className="grid grid-cols-1 gap-21 lg:grid-cols-[1fr_320px]" data-testid="editable-console">
      {/* editable surfaces */}
      <div className="space-y-16">
        <div className="rounded-lg border border-info bg-info-soft p-16 text-body-sm">
          <strong>You are editing clinical values, not code.</strong> Signed edits apply through the
          validated write path (schema → preflight → audit), recorded against this exact version and
          hash. Structure and engine behaviour are fixed — comment on those below, don’t edit them.
        </div>

        <div className="flex flex-wrap gap-x-16 gap-y-2 text-[12px] text-text-secondary" data-testid="cell-legend">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-info bg-info-soft" /> current value — confirm/change</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-warning bg-warning-soft" /> you changed it</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-flagged bg-flagged-soft" /> gap — needs your value</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-surface-sunken" /> locked — structure/code</span>
        </div>

        {bySection.map(([section, sectionCells]) => (
          <section key={section} className="rounded-lg border border-border p-21">
            <h3 className="text-base font-semibold">{sectionLabel(section)}</h3>
            {SECTION_HINT[section] ? <p className="mb-13 mt-1 text-body-sm text-text-secondary">{SECTION_HINT[section]}</p> : <div className="mb-13" />}
            <div className="space-y-10">
              {sectionCells.map((cell) => (
                <CellWidget
                  key={cell.path} cell={cell} value={values[cell.path] ?? ''}
                  baseline={baselineValues(cells)[cell.path] ?? ''}
                  onChange={(v) => setCell(cell.path, v)}
                />
              ))}
            </div>
          </section>
        ))}

        {/* structure-change comment — routed to the team, never the registry */}
        <section className="rounded-lg border border-dashed border-border-strong bg-surface-sunken p-21">
          <h3 className="text-base font-semibold">Structure &amp; engine <span className="font-normal text-text-secondary">— comment only</span></h3>
          <p className="mb-8 mt-1 text-body-sm text-text-secondary">
            The registry’s shape — which categories exist, how rules match — is fixed. If you believe
            the shape is wrong, raise it here. This routes to the Bioweave team; your clinical values
            above are unaffected.
          </p>
          <textarea
            data-testid="structure-comment" value={comment}
            onChange={(e) => { setComment(e.target.value); setCommentRouted(false); }}
            placeholder="e.g. FSH bands should be split by ethnicity — the single default band is too coarse."
            className="min-h-[64px] w-full resize-y rounded border border-border-strong bg-bg px-13 py-8 text-body-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
          />
          <div className="mt-8 flex items-center gap-13">
            <Button variant="outline" size="sm" disabled={!comment.trim() || commentM.isPending} onClick={() => commentM.mutate()}>
              {commentM.isPending ? 'Filing…' : 'File comment for team'}
            </Button>
            {commentRouted ? <span className="text-body-sm text-success" data-testid="comment-routed">✓ Routed to the team — not applied to the registry.</span> : null}
          </div>
        </section>
      </div>

      {/* rail: change-set + sign-off */}
      <div className="space-y-16 lg:sticky lg:top-21 lg:self-start">
        <section className="rounded-lg border border-border bg-surface p-16">
          <h3 className="mb-8 text-base font-semibold">Your change-set</h3>
          {changes.length === 0 ? (
            <p className="text-body-sm text-text-muted" data-testid="changeset-empty">
              No edits yet. Change a value and it’s captured here — before → after, keyed to v{detail.version}.
            </p>
          ) : (
            <ul className="space-y-8" data-testid="changeset-list">
              {changes.map((c) => (
                <li key={c.path} className="rounded border border-border p-8 text-[12px]">
                  <div className="flex items-start justify-between gap-8">
                    <span className="font-mono text-info">{c.label}</span>
                    <button className="text-text-muted hover:text-flagged" onClick={() => revertCell(c.path)}>revert</button>
                  </div>
                  <div className="mt-1">
                    <span className="text-flagged line-through">{changeDisplay(c.kind, c.from)}</span>
                    {' → '}
                    <span className="font-semibold text-success">{changeDisplay(c.kind, c.to)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-lg border border-border bg-surface p-16">
          <h3 className="mb-8 text-base font-semibold">Sign-off</h3>
          <span className="mb-13 block font-mono text-[11px] text-text-muted">v{detail.version} · {detail.version_hash.slice(0, 12)}…</span>

          {conflict ? (
            <div className="mb-13 rounded border border-warning bg-warning-soft p-13 text-body-sm" data-testid="conflict-banner">
              The registry changed since you loaded it. <button className="font-semibold underline" onClick={onChanged}>Reload the fresh values</button> and re-review before signing.
            </div>
          ) : null}

          <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-text-secondary">Name &amp; credentials</label>
          <input
            data-testid="attested-signatory" value={attested} onChange={(e) => setAttested(e.target.value)}
            placeholder="e.g. Dr … , FRACGP"
            className="mb-13 w-full rounded border border-border-strong bg-bg px-13 py-8 text-body-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
          />

          <div className="mb-8 flex flex-col gap-5">
            <label className="flex items-center gap-2 text-body-sm">
              <input type="radio" name="edit-decision" checked={decision === 'approve'} onChange={() => setDecision('approve')} />
              Approve {changes.length > 0 ? <span className="text-info">({changes.length} change{changes.length > 1 ? 's' : ''})</span> : <span className="text-text-muted">as reviewed</span>}
            </label>
            <label className="flex items-center gap-2 text-body-sm">
              <input type="radio" name="edit-decision" checked={decision === 'request_changes'} onChange={() => setDecision('request_changes')} />
              Cannot approve / request changes
            </label>
          </div>
          {decision === 'request_changes' ? (
            <textarea
              data-testid="edit-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What needs to change? (required)"
              className="mb-8 min-h-[56px] w-full resize-y rounded border border-border-strong bg-bg px-13 py-8 text-body-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
            />
          ) : null}

          {!readiness.ready ? <p className="mb-8 text-body-sm text-warning" data-testid="readiness">{readiness.reason}</p> : null}

          <Button
            data-testid="edit-sign-submit" size="sm" className="w-full"
            disabled={!readiness.ready || signM.isPending}
            onClick={() => signM.mutate()}
          >
            {signM.isPending ? 'Recording…' : decision === 'approve' ? 'Sign & submit' : 'Record request'}
          </Button>
          {signM.isError && !conflict ? (
            <p className="mt-8 text-body-sm text-flagged" data-testid="sign-error">
              {signM.error instanceof ConsoleApiError ? signM.error.detail : 'Could not record the sign-off.'}
            </p>
          ) : null}
          {signM.isSuccess ? <p className="mt-8 text-body-sm text-success" data-testid="sign-success">✓ Recorded. The trail now holds this decision.</p> : null}

          <div className="mt-13 border-t border-border pt-13">
            <button
              data-testid="revert-last" className="text-body-sm text-text-secondary hover:text-flagged disabled:opacity-40"
              disabled={revertM.isPending || attested.trim().length < 3}
              onClick={() => revertM.mutate()}
              title={attested.trim().length < 3 ? 'Enter your name to revert' : undefined}
            >
              {revertM.isPending ? 'Reverting…' : 'Revert the last approved edit'}
            </button>
            {revertM.isError && !conflict ? (
              <p className="mt-2 text-body-sm text-flagged">
                {revertM.error instanceof ConsoleApiError ? revertM.error.detail : 'Could not revert.'}
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

function CellWidget({
  cell, value, baseline, onChange,
}: {
  cell: EditableCell; value: CellValue; baseline: CellValue; onChange: (v: CellValue) => void;
}) {
  const dirty = String(value) !== String(baseline);
  // A gap = a required cell the MA hasn't set yet (the classifier hands it to
  // the LLM until signed). The ratification form paints these red — distinct
  // from amber "you changed it" and blue "current value".
  const gap = Boolean(cell.required) && (String(value) === 'MA_REQUIRED' || String(value) === '');
  const base = 'rounded border px-8 py-1.5 text-body-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus';
  const tone = gap ? 'border-flagged bg-flagged-soft text-flagged'
    : dirty ? 'border-warning bg-warning-soft text-warning'
    : 'border-info bg-info-soft text-info';

  if (cell.locked) {
    return (
      <div className="flex items-center justify-between gap-8 text-body-sm">
        <span className="text-text-secondary">{cell.label}</span>
        <span className="rounded bg-surface-sunken px-8 py-1 text-[12px] text-text-muted" title={cell.lock_reason ?? undefined}>
          {String(cell.value)} · locked{cell.lock_reason ? ` (${cell.lock_reason})` : ''}
        </span>
      </div>
    );
  }

  // free-text cells (e.g. genomic interpretation strings) stack full-width
  if (cell.kind === 'text') {
    return (
      <div>
        <div className="mb-1 flex items-center gap-2">
          <span className="font-mono text-[11px] text-text-secondary">{cell.label}</span>
          {dirty ? <span className="rounded-pill bg-warning-soft px-2 py-0.5 text-[10px] font-semibold uppercase text-warning">edited</span> : null}
        </div>
        <textarea
          data-testid={`cell:${cell.path}`} value={String(value)}
          onChange={(e) => onChange(e.target.value)} rows={2}
          className={`min-h-[52px] w-full resize-y rounded border px-8 py-1.5 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus ${dirty ? 'border-warning bg-warning-soft' : 'border-border-strong bg-bg text-text-primary'}`}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-8">
      <span className="text-body-sm text-text-secondary">
        {cell.label}
        {cell.required && String(value) === 'MA_REQUIRED'
          ? <span className="ml-2 rounded-pill bg-flagged-soft px-2 py-0.5 text-[10px] font-semibold uppercase text-flagged">gap — needs your value</span>
          : null}
        {cell.kind === 'enum' && cell.section === 'disposition_table'
          ? <span className={`ml-2 font-mono text-[11px] ${determinateFromStance(value) ? 'text-success' : 'text-text-muted'}`}>determinate:{String(determinateFromStance(value))}</span>
          : null}
      </span>
      {cell.kind === 'number' ? (
        <input
          type="number" data-testid={`cell:${cell.path}`}
          value={String(value)} min={cell.constraint?.min} max={cell.constraint?.max} step={cell.constraint?.step ?? 0.5}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          className={`w-24 ${base} ${tone}`}
        />
      ) : (
        <select
          data-testid={`cell:${cell.path}`} value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          className={`min-w-[180px] ${base} ${tone}`}
        >
          {String(value) === 'MA_REQUIRED' ? <option value="MA_REQUIRED">— unsigned —</option> : null}
          {(cell.options ?? []).map((o) => <option key={o} value={o}>{stanceLabel(o)}</option>)}
        </select>
      )}
    </div>
  );
}
